import React, { useCallback, useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";

import { makeStyles } from "@material-ui/core/styles";
import Loading from "../../../../common/Loading";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import InputAdornment from "@material-ui/core/InputAdornment";

import Button from "components/CustomButtons/Button";
import Close from "@material-ui/icons/Close";
import { TextField } from "@material-ui/core";

const validationSchema = Yup.object().shape({
  amounts: Yup.array().of(
    Yup.object().shape({
      description: Yup.string().required("Description is required"),
      amount: Yup.number().min(0, "Min value 0.").required("Amount is required")
    })
  )
});

const useStyles = makeStyles(theme => ({
  dialogActions: {
    display: "flex",
    justifyContent: "center",
    marginTop: theme.spacing(2)
  },
  marginBottom: {
    marginBottom: theme.spacing(3)
  },
  marginRight: {
    marginRight: theme.spacing(3)
  },
  actions: {
    margin: "0 0 0 5px",
    padding: 5,
    "& svg": {
      margin: 0
    }
  }
}));

const EditExpenseDialog = ({ open, data, onClose, onSave }) => {
  const classes = useStyles();

  const parsedData = data.map(item => {
    return {
      ...item,
      amount: item.amount ? item.amount.toFixed(2) : 0
    };
  });

  const [isDirty, setDirty] = useState(false);

  const handleSubmit = useCallback(
    values => {
      setDirty(true);
      const items = values.amounts;
      const editedItems = items.filter(item =>
        parsedData.find(
          newItem =>
            newItem.id === item.id &&
            (item.description !== newItem.description ||
              item.amount !== newItem.amount)
        )
          ? true
          : false
      );
      const removedItems = parsedData.filter(item =>
        items.find(newItem => newItem.id === item.id) ? false : true
      );
      onSave({
        items,
        editedItems,
        removedItems
      });
    },
    [parsedData]
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Chapter Financial Information</DialogTitle>
      <DialogContent>
        <Grid container direction="column">
          <Typography>
            <strong>Operating Expenses</strong>
          </Typography>
          <Typography className={classes.marginBottom}>
            Please fill in the dollar amount received for each of the items
            below (if applicable).
          </Typography>
          <Formik
            initialValues={{ amounts: parsedData }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({
              values,
              touched,
              errors,
              setValues,
              handleChange,
              handleBlur,
              isValid
            }) => (
              <Form noValidate autoComplete="off">
                {values.amounts.map((item, index) => {
                  return (
                    <Grid item key={`formik-finance-${index}`}>
                      <Grid container alignItems="center" spacing={2}>
                        <Grid item xs={8}>
                          <Typography>{item.description}</Typography>
                        </Grid>
                        <Grid item xs={3}>
                          <TextField
                            fullWidth
                            variant="outlined"
                            name={`amounts[${index}].amount`}
                            value={item.amount}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  $
                                </InputAdornment>
                              )
                            }}
                            onChange={handleChange}
                            error={Boolean(
                              touched.amounts &&
                                touched.amounts[index] &&
                                touched.amounts[index].amount &&
                                errors.amounts &&
                                errors.amounts[index] &&
                                errors.amounts[index].amount
                            )}
                            helperText={
                              touched.amounts &&
                              touched.amounts[index] &&
                              touched.amounts[index].amount &&
                              errors.amounts &&
                              errors.amounts[index] &&
                              errors.amounts[index].amount &&
                              "Enter a valid number"
                            }
                          />
                        </Grid>
                        <Grid item xs={1}>
                          <Button
                            simple
                            color="danger"
                            onClick={() => {
                              setValues({
                                amounts: values.amounts.filter(
                                  newItem => newItem.id !== item.id
                                )
                              });
                            }}
                            className={classes.actions}
                          >
                            <Close />
                          </Button>
                        </Grid>
                      </Grid>
                    </Grid>
                  );
                })}
                <div className={classes.dialogActions}>
                  {isDirty ? (
                    <Loading />
                  ) : (
                    <>
                      <Button
                        className={classes.marginRight}
                        color="success"
                        variant="contained"
                        type="submit"
                      >
                        Submit
                      </Button>
                      <Button
                        color="danger"
                        variant="contained"
                        onClick={onClose}
                      >
                        Cancel
                      </Button>
                    </>
                  )}
                </div>
              </Form>
            )}
          </Formik>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default EditExpenseDialog;
